import type { Hit } from 'instantsearch.js'
import type { TrialLocationNarrow } from '../types/TrialAssociatedWithLocations'

// Number of trials to display per "subpage" as search results to the user
export const ITEMS_PER_SUBPAGE = 30
export const MINIMUM_RESULTS_THRESHOLD = 5

// Fallback Geolocation Values for when the user does not have a location (middle of the US)
export const FALLBACK_GEOLOCATION = {
  latitude: 39.8283,
  longitude: -98.5795,
}

export type PrimarySearchFilters = {
  city?: string
  condition?: string
  hospital?: string
  paid?: string
  state?: string
  treatment?: string
}

// Map each facet to its associated "s, TrialLocationNarrowtop word" or prefix
export const FACET_PREFIX_MAP = {
  city: 'near-',
  condition: 'for-',
  hospital: 'at-',
  paid: 'paid',
  state: 'within-',
  treatment: 'using-',
} as const

export type FacetType = keyof typeof FACET_PREFIX_MAP

export const LOCATION_FACETS = ['city', 'state', 'hospital'] as const

export const FACET_ORDER: FacetType[] = [
  'condition',
  'treatment',
  'city',
  'state',
  'hospital',
  'paid',
]

export const SECONDARY_FILTERS = [
  'phaseList',
  'overallStatus',
  'isPaidTrial',
] as const

export type SecondaryFilterParam = (typeof SECONDARY_FILTERS)[number]
export type SecondaryFilterParams = {
  [K in SecondaryFilterParam]: string | null
}

export const FILTER_VISIBILE = 'showFilters'

// Algolia search configuration constants
export const ALGOLIA_AROUND_RADIUS = 80000
export const ALGOLIA_HITS_PER_PAGE = 250
export const ALGOLIA_MAX_VALUES_PER_FACET = 10

export type AlgoliaNarrowTrialRecord = {
  _geoloc: { lat: number; lng: number }[]
  _highlightResult?: object
  _rankingInfo?: object
  activeContractWithSponsor: boolean
  biologicalSex: string
  briefSummary: string
  briefTitle: string
  conditions: string[]
  conjuredTitle: string
  currentRank: string
  enrollmentCount: number
  hasBackgroundTherapyAllowed: boolean
  hasNoPlacebo: boolean
  locationCities: string[]
  locationFacilities: string[]
  locationStates: string[]
  maximumAge: string
  medicationsDisqualifyingParticipation: string
  medicationsRequiredForParticipation: string
  minimumAge: string
  nctId: string
  nlpBriefSummary: string
  objectID: string
  overallStatus: string
  participationDisqualifiers: string
  phaseList: string[]
  primaryTreatment: string
  slug: string
  studyStartDate: string
  trialLocations: TrialLocationNarrow[]
}

export type AlgoliaTrialHit = Hit & Partial<AlgoliaNarrowTrialRecord>

export const SEARCH_LANDER_ATTRIBUTES = [
  '_geoloc',
  'activeContractWithSponsor',
  'currentRank',
  'locationStates',
  'locationCities',
  'locationFacilities',
  'phaseList',
  'conditions',
  'briefTitle',
  'nlpBriefSummary',
  'briefSummary',
  'conjuredTitle',
  'interventions',
  'minimumAge',
  'maximumAge',
  'participationDisqualifiers',
  'medicationsDisqualifyingParticipation',
  'medicationsRequiredForParticipation',
  'biologicalSex',
  'overallStatus',
  'hasNoPlacebo',
  'primaryTreatment',
  'hasBackgroundTherapyAllowed',
  'enrollmentCount',
  'slug',
  'nctId',
  'trialLocations',
  'isResponsive',
  'studyStartDate',
]

export const ALGOLIA_FACETS = [
  'conditions',
  'interventions',
  'isPaidTrial',
  'locationCities',
  'locationFacilities',
  'locationStates',
  'overallStatus',
  'phaseList',
]

export const CONDITION_SUGGESTIONS = [
  'Depression',
  'Schizophrenia',
  'Bipolar Disorder',
  'Anxiety Disorder',
  'Dysthymia',
]

// Define the desired order of phases
export const STATUS_LABELS = {
  ActiveNotRecruiting: 'Active Not Recruiting',
  EnrollingByInvitation: 'Enrolling By Invitation',
  NotYetRecruiting: 'Not Yet Recruiting',
  Recruiting: 'Recruiting',
}
